import React, {
  lazy,
  ReactElement,
  ReactNode,
  Suspense,
  useCallback,
  useMemo,
  useState
} from 'react';
import Box from '@material-ui/core/Box';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import PropTypes from 'prop-types';
import AppLoading from 'shared/components/ui/AppLoading';
import BackToTop from 'shared/components/ui/BackToTop';
import PageTitle from 'shared/components/ui/PageTitle';
import BreadcrumbContextProvider from 'shared/contexts/BreadcrumbContext/BreadcrumbContextProvider';
import useUserContext from 'shared/contexts/UserContext/useUserContext';
import useIsMobile from 'shared/hooks/useIsMobile';
import Header from './Header';
import useStyles from './useStyles';

const DoctorSidebar = lazy(() => import('./Sidebar/DoctorSidebar'));
const PatientSidebar = lazy(() => import('./Sidebar/PatientSidebar'));

type Props = {
  children: ReactNode;
  title: string;
};

function LoggedInLayout({children, title}: Props): ReactElement {
  const isMobile = useIsMobile();
  const variant = isMobile ? 'temporary' : 'permanent';
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const classes = useStyles({isCollapsed: isSidebarCollapsed});
  const {isDoctor} = useUserContext();

  const toggleSidebar = useCallback(function() {
    setIsSidebarCollapsed(value => !value);
  }, []);

  const UserSidebar = isDoctor ? DoctorSidebar : PatientSidebar;

  const swipableDrawerClasses = useMemo(
    () => ({
      paper: classes.swipableDrawerPaper
    }),
    [classes.swipableDrawerPaper]
  );

  const backToTopId = 'header';
  return (
    <Box className={classes.container} id={backToTopId}>
      <SwipeableDrawer
        classes={swipableDrawerClasses}
        anchor="left"
        open={isSidebarCollapsed}
        onClose={toggleSidebar}
        onOpen={toggleSidebar}
        variant={variant}
      >
        <Suspense fallback={<AppLoading />}>
          <UserSidebar />
        </Suspense>
      </SwipeableDrawer>

      <Box className={classes.contentWrapper}>
        <Header onClickHamburger={toggleSidebar} />
        {title && <PageTitle>{title}</PageTitle>}
        <BreadcrumbContextProvider>{children}</BreadcrumbContextProvider>
      </Box>
      <BackToTop anchorId={backToTopId} />
    </Box>
  );
}

LoggedInLayout.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.elementType, PropTypes.node]),
  title: PropTypes.string
};

LoggedInLayout.defaultProps = {
  title: null
};

export default LoggedInLayout;
